import {
  convertToAssetId,
  extractAssetOwnerId,
  FolderTreeItem,
  HydratedAsset,
  HydratedZapAsset,
} from "@zapier/client";
import { BulkModal, ZapModal } from "@zapier/shared";
import { AssetTypes } from "@zapier/utils";
import { useCallback, useMemo, useState } from "react";
import { useBulkSelectItems } from "./useBulkSelectItems";
import type { SingleRestore } from "./useRestoreNotification";

interface BulkCopyState {
  assets: HydratedAsset[];
  folderIds?: string[];
  selectedItemsAreAllFolders?: boolean;
}

interface Input {
  folder: FolderTreeItem | undefined;
  folderId?: string;
  assets: HydratedAsset[];
  filteredSubfolders: FolderTreeItem[];
  onMoveClicked?: (ids: string[]) => void;
  isCopyToAnotherAccountEnabled?: boolean;
}

function getTypedAssetId(asset: HydratedAsset) {
  return convertToAssetId(asset.type as AssetTypes, asset.id);
}

/** Extracts the display name + previous folder used to name a single restore in the toast. */
export function getSingleRestoreMeta(asset: HydratedAsset): SingleRestore {
  const assetName =
    asset.type === AssetTypes.Zap && "title" in asset
      ? (asset.title as string) || undefined
      : "name" in asset
        ? (asset.name as string) || undefined
        : undefined;

  const folder =
    "folder" in asset && asset.folder
      ? { id: asset.folder.id, name: asset.folder.name }
      : undefined;

  // Ownership lives in different fields per asset type (customuserId for zaps,
  // ownerId for canvas, etc.); extractAssetOwnerId normalizes them.
  const ownerId = extractAssetOwnerId(asset);
  const owner = ownerId != null ? { id: ownerId } : undefined;

  return { assetName, folder, owner };
}

function canShowBulkCopyAction(ids: string[]) {
  return ids.length > 0;
}

function buildFolderParentIdMap(folders: FolderTreeItem[]) {
  const parentByFolderId = new Map<string, string | undefined>();
  const queue = folders.map((folder) => ({
    folder,
    parentId: folder.parentId,
  }));

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      continue;
    }

    const { folder: currentFolder, parentId } = current;
    parentByFolderId.set(currentFolder.id, parentId ?? undefined);

    for (const child of currentFolder.children || []) {
      queue.push({
        folder: child as FolderTreeItem,
        parentId: currentFolder.id,
      });
    }
  }

  return parentByFolderId;
}

function dedupeFolderSelectionsByAncestor(
  selectedFolderIds: string[],
  parentByFolderId: Map<string, string | undefined>,
) {
  const uniqueSelectedFolderIds = [...new Set(selectedFolderIds)];
  const selectedFolderIdSet = new Set(uniqueSelectedFolderIds);

  return uniqueSelectedFolderIds.filter((folderId) => {
    let parentId = parentByFolderId.get(folderId);

    while (parentId) {
      if (selectedFolderIdSet.has(parentId)) {
        return false;
      }
      parentId = parentByFolderId.get(parentId);
    }

    return true;
  });
}

export function useBulkSelectState({
  folder,
  folderId,
  assets,
  filteredSubfolders,
  onMoveClicked,
  isCopyToAnotherAccountEnabled = true,
}: Input) {
  const [currentZapModal, setCurrentZapModal] = useState<
    ZapModal | undefined
  >();
  const [currentBulkModal, setCurrentBulkModal] = useState<
    BulkModal | undefined
  >();
  const [selectedAssetIds, setSelectedAssetIds] = useState<
    string[] | undefined
  >();
  const [bulkCopyState, setBulkCopyState] = useState<
    BulkCopyState | undefined
  >();

  const clearSelection = useCallback(() => {
    setSelectedAssetIds(undefined);
    setBulkCopyState(undefined);
  }, [setSelectedAssetIds]);

  const selectedZaps = useMemo(() => {
    if (!selectedAssetIds) {
      return [] as HydratedZapAsset[];
    }
    const zaps = assets.filter(
      (asset) => asset.type === AssetTypes.Zap,
    ) as HydratedZapAsset[];
    return zaps.filter((zap) =>
      selectedAssetIds.some((id) => id === zap.id || id === `zap:${zap.id}`),
    );
  }, [selectedAssetIds, assets]);

  const selectedTypedAssetIds = useMemo(() => {
    return selectedAssetIds || [];
  }, [selectedAssetIds]);

  const folderParentIdMap = useMemo(
    () => buildFolderParentIdMap(filteredSubfolders),
    [filteredSubfolders],
  );

  const assetsByTypedId = new Map<string, HydratedAsset>();
  for (const asset of assets) {
    assetsByTypedId.set(getTypedAssetId(asset), asset);
  }

  const getSelectionSummary = (ids: string[]) => {
    const selectedFolderIds: string[] = [];
    const selectedAssets: HydratedAsset[] = [];

    for (const id of ids) {
      if (id.startsWith("folder:")) {
        selectedFolderIds.push(id.replace("folder:", ""));
        continue;
      }

      const asset = assetsByTypedId.get(id);
      if (asset) selectedAssets.push(asset);
    }

    const dedupedFolderIds = dedupeFolderSelectionsByAncestor(
      selectedFolderIds,
      folderParentIdMap,
    );

    return {
      selectedAssets,
      selectedFolderIds: dedupedFolderIds,
    };
  };

  const handleCopyToAnotherAccount = (ids: string[]) => {
    const { selectedAssets, selectedFolderIds } = getSelectionSummary(ids);
    const hasSelectedAssets = selectedAssets.length > 0;
    const selectedFolderCount = selectedFolderIds.length;
    const hasSelectedFolders = selectedFolderCount > 0;
    const hasSingleSelectedFolder = selectedFolderCount === 1;
    const isMixedAssetAndFolderSelection =
      hasSelectedAssets && hasSelectedFolders;

    // Mixed + single folder
    if (isMixedAssetAndFolderSelection && hasSingleSelectedFolder) {
      const selectedFolderId = selectedFolderIds[0];
      setBulkCopyState({
        assets: selectedAssets,
        folderIds: [selectedFolderId],
        selectedItemsAreAllFolders: false,
      });
      setCurrentBulkModal(BulkModal.CopyToAnotherAccount);
      return;
    }

    // Any assets (assets-only or mixed multi-folder)
    if (hasSelectedAssets) {
      setBulkCopyState({
        assets: selectedAssets,
        ...(hasSelectedFolders ? { folderIds: selectedFolderIds } : {}),
        selectedItemsAreAllFolders: false,
      });
      setCurrentBulkModal(BulkModal.CopyToAnotherAccount);
      return;
    }

    // Single folder only
    if (hasSingleSelectedFolder) {
      const selectedFolderId = selectedFolderIds[0];
      setBulkCopyState({
        assets: [],
        folderIds: [selectedFolderId],
        selectedItemsAreAllFolders: true,
      });
      setCurrentBulkModal(BulkModal.CopyToAnotherAccount);
      return;
    }

    // Fallback (multi-folder only / no assets)
    setBulkCopyState({
      assets: [],
      ...(hasSelectedFolders ? { folderIds: selectedFolderIds } : {}),
      selectedItemsAreAllFolders: hasSelectedFolders,
    });
    setCurrentBulkModal(BulkModal.CopyToAnotherAccount);
  };

  const resolveRestoreMeta = useCallback(
    (id: string): SingleRestore | undefined => {
      const asset = assets.find(
        (candidate) => getTypedAssetId(candidate) === id,
      );
      return asset ? getSingleRestoreMeta(asset) : undefined;
    },
    [assets],
  );

  const bulkSelectItems = useBulkSelectItems({
    folder,
    folderId,
    setCurrentZapModal,
    setSelectedAssetIds,
    setCurrentBulkModal,
    onMoveClicked,
    onCopyToAnotherAccountClicked: handleCopyToAnotherAccount,
    isCopyToAnotherAccountVisible: (ids) =>
      isCopyToAnotherAccountEnabled && canShowBulkCopyAction(ids),
    resolveRestoreMeta,
  });

  return {
    selectedZaps,
    selectedAssetIds,
    setSelectedAssetIds,
    selectedTypedAssetIds,
    bulkSelectItems,
    clearSelection,
    setCurrentZapModal,
    currentZapModal,
    currentBulkModal,
    setCurrentBulkModal,
    bulkCopyState,
  };
}
